/**
 * Scatter renderer (blob `renderScatter`). A custom `fillRect`
 * draw hook replaces uPlot's per-point `arc()` (which stalls at ~50k points);
 * color-by quantizes the scale into 64 buckets so `fillStyle` is set N times,
 * not per point. Optional y=x diagonal and dashed fit overlays draw on top.
 * Writes the color-legend projection for `<plot-chart>`.
 */

import type uPlot from 'uplot';
import { plotState } from '../../../state/plot-state.ts';
import {
  buildSeriesColorer,
  fitColorFor,
  colorScaleGradientCss,
  PLOT_COLORS,
  DISCRETE_SCALE,
  isDiscreteScale,
  distinctColorValues,
  formatColorValue,
} from '../plot-constants.ts';
import { colorScaleRgb, discreteColors } from './color-scale.ts';
import { withAlpha } from './format.ts';
import { themeColors, chartContainerSize } from './chart-dom.ts';
import {
  applyAxisBounds,
  buildLegendOpts,
  buildXAxis,
  buildYAxis,
  yAxisTitle,
  utcTzDate,
  rebuildUplot,
  applyHoverFocus,
} from './uplot-helpers.ts';
import { drawDiagonal, drawColorLegendBox } from './overlays.ts';
import { updateTooltip } from './tooltip.ts';
import { onXScaleChanged } from '../zoom.ts';
import { colorLegend, plotFocusShip } from '../plot-ui-state.ts';
import type { PlotResponse } from '../../../api/types.ts';

type NumArr = Array<number | null>;
const dpr = (): number => window.devicePixelRatio || 1;

/**
 * Whether a series index is currently visible.
 *
 * Scatter paints its points in custom draw hooks rather than through uPlot's
 * own path rendering (that is what makes the colour-by gradient possible), so
 * uPlot's own `show` handling never applies. Without consulting it here,
 * clicking a legend row - or Select/Deselect all - changes `show` and redraws,
 * and the points come back exactly as before.
 */
function seriesVisible(u: uPlot, si: number): boolean {
  return u.series[si]?.show !== false;
}

export function renderScatter(data: PlotResponse): void {
  const c = themeColors();
  const s = plotState.value;

  // Scatter rows arrive sorted by x; do NOT dedupe - duplicate x are real.
  const sanitized = { x: data.x.slice(), series: data.series.map((ser) => ser.values.slice()) };

  const hasColorBy = !!data.color_values && data.color_values.length === data.x.length;
  const colorVals = hasColorBy ? data.color_values!.slice() : null;
  let cMin = 0;
  let cMax = 1;
  if (hasColorBy && colorVals) {
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < colorVals.length; i++) {
      const v = colorVals[i];
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    if (lo !== Infinity) {
      cMin = lo;
      cMax = hi === lo ? lo + 1 : hi;
    }
  }

  const alpha = Math.max(0, Math.min(1, s.style.scatter_alpha ?? 0.7));

  const hasRight = data.series.some((ser) => ser.axis === 'right');

  const uData: uPlot.AlignedData = [sanitized.x as number[]];
  // See render-line: ship-aware for multi-ship, unchanged for one ship.
  const colorOf = buildSeriesColorer(s, data.series, plotFocusShip.value);
  const seriesOpts: uPlot.Series[] = [{}];
  const seriesSolidColors: string[] = [];
  // Scale name per drawn series, indexed as uData is (0 is x). The point clouds
  // are painted by hand below, so every `valToPos` has to be told which y scale
  // the value belongs to - uPlot only does that for series it draws itself.
  const seriesScales: string[] = ['x'];
  data.series.forEach((ser, i) => {
    const color = colorOf(ser, i);
    seriesSolidColors.push(color);
    const scale = ser.axis === 'right' && hasRight ? 'yR' : 'y';
    seriesScales.push(scale);
    seriesOpts.push({
      label: ser.name,
      scale,
      stroke: hasColorBy ? 'transparent' : withAlpha(color, alpha),
      paths: () => null,
      points: { show: false },
    });
    uData.push(sanitized.series[i] as NumArr);
  });

  // Fit overlays declared as series for tooltip/legend, but path drawing is
  // suppressed (drawn manually after the point cloud so they sit on top).
  const nScatterMain = data.series.length;
  const scatterFits = data.fits.filter(
    (f) => !f.error && Array.isArray(f.y) && f.y!.length === data.x.length,
  );
  scatterFits.forEach((f) => {
    const baseIdx = data.series.findIndex((ser) => ser.name === f.series);
    const color = baseIdx >= 0 ? fitColorFor(s, f.series, baseIdx) : '#888';
    // A fit rides the axis of the series it fits, or it lands nowhere near it.
    const fitScale = baseIdx >= 0 ? seriesScales[1 + baseIdx]! : 'y';
    seriesScales.push(fitScale);
    seriesOpts.push({
      label: `${f.series} · fit`,
      scale: fitScale,
      stroke: color,
      width: 2.5,
      dash: [7, 4],
      spanGaps: true,
      paths: () => null,
      points: { show: false },
    });
    uData.push(f.y!.slice() as NumArr);
  });

  const scales: Record<string, uPlot.Scale> = { x: { time: !!data.x_is_time }, y: {} };
  if (hasRight) scales.yR = {};
  applyAxisBounds(scales, !!data.x_is_time, hasRight);

  const leftLabel = yAxisTitle(data.series, 'left', s.axis_titles.y);
  const rightLabel = yAxisTitle(data.series, 'right', s.axis_titles.yr);
  const axes: uPlot.Axis[] = [
    buildXAxis(c, !!data.x_is_time, data.x_column || (data.x_is_time ? 'Time' : 'x')),
    buildYAxis(c, 'y', 'left', leftLabel),
  ];
  if (hasRight) axes.push(buildYAxis(c, 'yR', 'right', rightLabel));

  const opts: uPlot.Options = {
    ...chartContainerSize(),
    scales,
    axes,
    series: seriesOpts,
    // No hover focus on scatter, deliberately: `cursor.focus` + `focus.alpha`
    // dim every series but the nearest one, which on a point cloud fades most
    // of the chart while the pointer is anywhere over it. Line and histogram
    // keep it - there, one hovered line out of a few is a useful highlight.
    cursor: {
      x: true,
      y: true,
      drag: { x: true, y: false, setScale: true },
    },
    legend: buildLegendOpts(data.series.length),
    hooks: {
      setCursor: [(u): void => updateTooltip(u, data, seriesOpts)],
      setScale: [
        (u, key): void => {
          if (key === 'x') onXScaleChanged(u, !!data.x_is_time);
        },
      ],
    },
  };

  // Custom point draw (fillRect, far faster than per-point arc()).
  const pointSizePx = s.style.scatter_point_size ?? 3;
  const POINT_SIZE = pointSizePx * dpr() * 2;
  const POINT_HALF = POINT_SIZE / 2;
  const drawHooks: Array<(u: uPlot) => void> = [];

  // Discrete-coloring legend, populated below when the "Discrete" scale is used.
  let discreteLegend: Array<{ label: string; color: string }> | null = null;

  if (hasColorBy && colorVals) {
    const scaleName = s.color_scale || 'Viridis';
    const nPts = colorVals.length;
    const categorical =
      !!data.color_categorical || colorVals.some((v) => typeof v === 'string');

    // Discrete (categorical): one palette color per distinct value. Used for a
    // categorical column, or when the user picked a "Discrete*" scale on a
    // low-cardinality numeric column. Numeric columns with too many distinct
    // values fall back to the continuous gradient below.
    const wantDiscrete = categorical || isDiscreteScale(scaleName);
    const discreteVals = wantDiscrete
      ? distinctColorValues(colorVals, PLOT_COLORS.length)
      : null;

    const N_BUCKETS = discreteVals ? discreteVals.length : 64;
    const bucketIdx = new Uint8Array(nPts);
    const bucketColors = new Array<string>(N_BUCKETS);

    if (discreteVals) {
      // A categorical column forced onto a gradient scale falls back to the
      // qualitative palette; an explicit "Discrete:<Colormap>" samples that map.
      const paletteScale = isDiscreteScale(scaleName) ? scaleName : DISCRETE_SCALE;
      const palette = discreteColors(paletteScale, discreteVals.length);
      const colorAt = (i: number): string =>
        palette[i] ?? PLOT_COLORS[i % PLOT_COLORS.length]!;
      const valToBucket = new Map<number | string, number>();
      discreteVals.forEach((v, i) => valToBucket.set(v, i));
      for (let i = 0; i < nPts; i++) {
        const cv = colorVals[i];
        if (cv != null) bucketIdx[i] = valToBucket.get(cv) ?? 0;
      }
      for (let b = 0; b < N_BUCKETS; b++) bucketColors[b] = colorAt(b);
      discreteLegend = discreteVals.map((v, i) => ({
        label: formatColorValue(v),
        color: colorAt(i),
      }));
    } else {
      for (let i = 0; i < nPts; i++) {
        const cv = colorVals[i];
        if (typeof cv === 'number' && Number.isFinite(cv)) {
          let t = (cv - cMin) / (cMax - cMin);
          if (t < 0) t = 0;
          else if (t > 1) t = 1;
          const b = (t * N_BUCKETS) | 0;
          bucketIdx[i] = b >= N_BUCKETS ? N_BUCKETS - 1 : b;
        }
      }
      for (let b = 0; b < N_BUCKETS; b++) bucketColors[b] = colorScaleRgb(scaleName, (b + 0.5) / N_BUCKETS);
    }
    const indicesByBucket: number[][] = Array.from({ length: N_BUCKETS }, () => []);
    for (let i = 0; i < nPts; i++) indicesByBucket[bucketIdx[i]!]!.push(i);

    drawHooks.push((u) => {
      const ctx = u.ctx;
      const xData = uData[0] as number[];
      const xLo = u.bbox.left;
      const xHi = u.bbox.left + u.bbox.width;
      const yLo = u.bbox.top;
      const yHi = u.bbox.top + u.bbox.height;
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = alpha;
      try {
        for (let si = 1; si <= nScatterMain; si++) {
          if (!seriesVisible(u, si)) continue;
          const yData = uData[si] as NumArr;
          for (let b = 0; b < N_BUCKETS; b++) {
            const idxs = indicesByBucket[b]!;
            if (!idxs.length) continue;
            ctx.fillStyle = bucketColors[b]!;
            for (let j = 0; j < idxs.length; j++) {
              const i = idxs[j]!;
              const xv = xData[i];
              const yv = yData[i];
              if (xv == null || yv == null || !Number.isFinite(xv) || !Number.isFinite(yv)) continue;
              const cx = u.valToPos(xv, 'x', true);
              const cy = u.valToPos(yv, seriesScales[si]!, true);
              if (cx < xLo || cx > xHi || cy < yLo || cy > yHi) continue;
              ctx.fillRect(cx - POINT_HALF, cy - POINT_HALF, POINT_SIZE, POINT_SIZE);
            }
          }
        }
      } finally {
        ctx.globalAlpha = prevAlpha;
      }
    });
  } else {
    drawHooks.push((u) => {
      const ctx = u.ctx;
      const xData = uData[0] as number[];
      const xLo = u.bbox.left;
      const xHi = u.bbox.left + u.bbox.width;
      const yLo = u.bbox.top;
      const yHi = u.bbox.top + u.bbox.height;
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = alpha;
      try {
        for (let si = 1; si <= nScatterMain; si++) {
          if (!seriesVisible(u, si)) continue;
          const yData = uData[si] as NumArr;
          ctx.fillStyle = seriesSolidColors[si - 1]!;
          for (let i = 0; i < xData.length; i++) {
            const xv = xData[i];
            const yv = yData[i];
            if (xv == null || yv == null || !Number.isFinite(xv) || !Number.isFinite(yv)) continue;
            const cx = u.valToPos(xv, 'x', true);
            const cy = u.valToPos(yv, seriesScales[si]!, true);
            if (cx < xLo || cx > xHi || cy < yLo || cy > yHi) continue;
            ctx.fillRect(cx - POINT_HALF, cy - POINT_HALF, POINT_SIZE, POINT_SIZE);
          }
        }
      } finally {
        ctx.globalAlpha = prevAlpha;
      }
    });
  }

  if (s.style.show_diagonal && !data.x_is_time) drawHooks.push(drawDiagonal);

  // Fits drawn on top of the cloud (after the fillRect loop).
  if (scatterFits.length) {
    const fitYData = scatterFits.map((_, fi) => uData[1 + nScatterMain + fi] as NumArr);
    const fitColors = scatterFits.map((f) => {
      const baseIdx = data.series.findIndex((ser) => ser.name === f.series);
      return baseIdx >= 0 ? fitColorFor(s, f.series, baseIdx) : '#888';
    });
    drawHooks.push((u) => {
      const ctx = u.ctx;
      const xData = uData[0] as number[];
      ctx.save();
      ctx.lineWidth = 2.5 * dpr();
      ctx.setLineDash([7 * dpr(), 4 * dpr()]);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      for (let fi = 0; fi < scatterFits.length; fi++) {
        // A fit hides with its own legend row, and with the series it fits -
        // a trend line floating over a hidden cloud is just noise.
        if (!seriesVisible(u, 1 + nScatterMain + fi)) continue;
        const baseIdx = data.series.findIndex((ser) => ser.name === scatterFits[fi]!.series);
        if (baseIdx >= 0 && !seriesVisible(u, 1 + baseIdx)) continue;
        const yArr = fitYData[fi]!;
        ctx.strokeStyle = fitColors[fi]!;
        ctx.beginPath();
        let started = false;
        for (let i = 0; i < xData.length; i++) {
          const xv = xData[i];
          const yv = yArr[i];
          if (xv == null || yv == null || !Number.isFinite(xv) || !Number.isFinite(yv)) {
            started = false;
            continue;
          }
          const cx = u.valToPos(xv, 'x', true);
          const cy = u.valToPos(yv, seriesScales[1 + nScatterMain + fi]!, true);
          if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
            started = false;
            continue;
          }
          if (!started) {
            ctx.moveTo(cx, cy);
            started = true;
          } else {
            ctx.lineTo(cx, cy);
          }
        }
        ctx.stroke();
      }
      ctx.restore();
    });
  }

  // Discrete color-by legend: draw it on the canvas (top-right) so it reads as
  // part of the plot and travels into image/PDF exports. Pushed last → on top.
  if (discreteLegend && data.color_by) {
    const legend = { title: data.color_by, items: discreteLegend };
    drawHooks.push((u) => drawColorLegendBox(u, legend));
  }

  opts.hooks!.draw = drawHooks;
  if (data.x_is_time) opts.tzDate = utcTzDate;

  applyHoverFocus(opts);
  rebuildUplot(uData, opts);

  // Below-chart color legend (DOM): only the continuous gradient now - the
  // discrete legend lives on the canvas (above), so it isn't duplicated here.
  if (hasColorBy && data.color_by && !discreteLegend) {
    colorLegend.set({
      colorBy: data.color_by,
      min: Number(cMin.toPrecision(4)),
      max: Number(cMax.toPrecision(4)),
      gradientCss: colorScaleGradientCss(s.color_scale),
    });
  } else {
    colorLegend.set(null);
  }
}
